import React, { Component } from "react";
import Header from "../../components/header/Header";
import { handleLandingScroll } from "../../utils/scroll";
import { trackPageview } from "../../utils/analytics";

function Role({ tag, weight, title, italic, stack, children }) {
  return (
    <div className="role-block">
      <div className="when">
        <b>{tag}</b>
        <span className="where">{weight}</span>
      </div>
      <div>
        <h3>{title} {italic ? <em>{italic}</em> : null}</h3>
        <div className="where-line">{stack}</div>
        <p>{children}</p>
      </div>
      <div className="side"></div>
    </div>
  );
}

function Phase({ id, title, italic, children }) {
  return (
    <div className="role-block">
      <div className="when"><b>PHASE {id}</b></div>
      <div>
        <h3>{title} {italic ? <em>{italic}</em> : null}</h3>
        <p>{children}</p>
      </div>
      <div className="side"></div>
    </div>
  );
}

class Experience extends Component {
  componentDidMount() {
    handleLandingScroll(this.props.location);
    trackPageview();
  }
  render() {
    return (
      <>
        <div className="grid-bg" />
        <Header theme={this.props.theme} />

        <div className="wrap">

          {/* HERO */}
          <section className="hero">
            <div className="label-strip">
              <span><b>HOW I WORK</b></span>
              <span>FREELANCE · REMOTE · 2020 →</span>
              <span><b>3 – 9</b> MONTHS / ENGAGEMENT</span>
            </div>

            <h1 className="huge">
              <span className="row">INDEP.</span>
              <span className="row indent"><span className="ital">end-to-end.</span></span>
            </h1>

            <div className="sub-row">
              <p>
                I work as a <b>software engineer</b> focused on web
                scraping, automation, and full-stack data products. Most
                engagements run three to nine months and end with something
                that quietly keeps running on its own.
              </p>
              <div className="meta-block">
                <div className="k">SHAPE</div>
                <div className="row"><span className="lab">SETUP</span><span>REMOTE · GMT+1</span></div>
                <div className="row"><span className="lab">START</span><span>FREELANCE 2020</span></div>
                <div className="row"><span className="lab">SLICE</span><span>VERTICAL E2E</span></div>
                <div className="row"><span className="lab">CLIENTS</span><span>SECTOR / NDA</span></div>
                <div className="row"><span className="lab">REPLY</span><span>&lt; 24H</span></div>
              </div>
            </div>
          </section>

          {/* ENGAGEMENT SHAPES */}
          <section className="section" id="engagements">
            <div className="section-head">
              <h2 className="section-title">01 <em>·</em> WHAT I'M USEFUL FOR</h2>
              <div className="meta"><b>04</b><br/>BUCKETS</div>
            </div>

            <div className="role-list">
              <Role
                tag="MOST OFTEN"
                weight="scraping"
                title="PRODUCTION"
                italic="web-scraping"
                stack="SCRAPY · PLAYWRIGHT · PYTHON · ANTI-BOT ROTATION · SCHEDULING"
              >
                Scrapers that survive site changes, observable enough to fail loudly when something drifts. Output goes into a database the client already has — or a new one if not.
              </Role>

              <Role
                tag="OFTEN"
                weight="pipelines"
                title="DATA PIPELINES"
                italic="& ETL"
                stack="PYTHON · PANDAS · POSTGRES · AIRFLOW / CRON · DBT-STYLE MODELS"
              >
                From scraped or vendor data to clean tables a team can actually query. Usually paired with a small reporting layer or a Sheets export.
              </Role>

              <Role
                tag="OFTEN"
                weight="full-stack"
                title="INTERNAL"
                italic="dashboards"
                stack="DJANGO / FASTAPI · REACT · NEXT.JS"
              >
                The UI on top of the data. Authentication, filtering, exports — the CRUD that makes the pipeline worth building in the first place.
              </Role>

              <Role
                tag="REGULARLY"
                weight="cloud"
                title="CLOUD SETUP"
                italic="& deploy"
                stack="AWS · GCP · AZURE · DOCKER · KUBERNETES · TERRAFORM"
              >
                VMs, managed databases, container orchestration, CI/CD. I prefer boring infrastructure over clever — easier to hand off when the engagement ends.
              </Role>

            </div>
          </section>

          {/* PHASES */}
          <section className="section">
            <div className="section-head">
              <h2 className="section-title">02 <em>·</em> HOW IT GOES</h2>
              <div className="meta"><b>04</b><br/>PHASES</div>
            </div>

            <div className="role-list">
              <Phase id="01" title="SCOPE" italic="& spike">
                Short kickoff to understand the actual goal (often different from the brief), then a one- or two-day spike on the riskiest unknown — usually whether the target source can be scraped at the needed scale.
              </Phase>

              <Phase id="02" title="THIN" italic="slice">
                End-to-end skeleton: one source, one transformation, one destination. Working, deployed, ugly. Used to validate the data with the people who'll actually consume it before scaling out.
              </Phase>

              <Phase id="03" title="SCALE" italic="& harden">
                Add the remaining sources, retries, observability, the dashboard or export the team needs, and the boring deployment story. This is where most of the time goes.
              </Phase>

              <Phase id="04" title="HAND-OFF" italic="or retainer">
                Documentation, runbooks, and a quiet exit — or a small ongoing retainer for the parts of the web that change unpredictably (e-commerce sites, social platforms, regulators).
              </Phase>
            </div>
          </section>

          {/* COMMUNITY */}
          <section className="section">
            <div className="section-head">
              <h2 className="section-title">03 <em>·</em> COMMUNITY</h2>
              <div className="meta"><b>OSS</b><br/>SINCE 2018</div>
            </div>

            <p style={{ fontSize: 17, lineHeight: 1.6, maxWidth: "62ch", marginBottom: 18 }}>
              GitHub Developer Program member · contributions to <b>Tensorflow</b>, <b>scikit-learn</b>, <b>Sympy</b>, <b>Kiwix</b>, <b>Keras</b> and a long tail of one-PR repos. Past: <b>Microsoft Student Partner</b>, <b>Google Explore ML</b> facilitator, <b>Mozilla Campus Captain</b>.
            </p>

            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em" }}>
              <a className="inline" href="#/opensource?to=repos">→ GITHUB DETAILS</a> · <a className="inline" href="#/cv">→ FULL CV</a>
            </p>
          </section>
        </div>

        {/* CONTACT SLAB */}
        <div className="wrap">
          <div className="foot-slab">
            <div className="inner">
              <h2>HAVE A SLICE <em>for me?</em></h2>
              <div>
                <p>I take on two or three engagements at a time. Tell me the shape, the timeline, and the rough scale.</p>
                <div className="links">
                  <a href="mailto:maaninou.walid@gmail.com">→ EMAIL <span>MAANINOU.WALID@GMAIL.COM</span></a>
                  <a href="https://www.linkedin.com/in/walid-maaninou-86a844102/">→ LINKEDIN <span>IN/WALID-MAANINOU</span></a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="site-footer">
          <span>© 2026 MAANINOU WALID · CASABLANCA · GMT+1</span>
          <span>
            <a href="mailto:maaninou.walid@gmail.com">EMAIL</a>
            <a href="https://github.com/walidMaaninou">GITHUB</a>
            <a href="https://www.linkedin.com/in/walid-maaninou-86a844102/">LINKEDIN</a>
          </span>
        </footer>
      </>
    );
  }
}

export default Experience;
